"use client"

import { clx } from "@medusajs/ui"
import { useEffect, useRef, useState } from "react"

const ANNOUNCEMENT_ROTATION_INTERVAL_MS = 5000
const ANNOUNCEMENT_FADE_DURATION_MS = 500

/** Pinned bar height; nav `top` and this must stay in sync. */
const ANNOUNCEMENT_BAR_HEIGHT_PX = 38

const ANNOUNCEMENTS = [
  "📦 Brezplačna dostava nad 50 €",
  "⭐ Ocena kupcev 4,6/5",
  "💌 5 % popust ob prijavi na novice",
  "🔒 Varno spletno plačilo",
  "🚚 Hitra dostava",
]

/**
 * Wraps the nav in a floating pill with cream background, border, and shadow.
 * Announcement strip is a separate fixed sibling (first in DOM) so it is not
 * nested inside the nav stack; rotation uses a fade-out then index change to
 * avoid two messages appearing at once.
 */
export function FloatingNavWrapper({
  children,
  categoryRow,
  className,
}: {
  children: React.ReactNode
  /** Optional second row (e.g. dark navy category bar) rendered below the cream pill. */
  categoryRow?: React.ReactNode
  className?: string
}) {
  const [announcementIndex, setAnnouncementIndex] = useState(0)
  const [isAnnouncementVisible, setIsAnnouncementVisible] = useState(true)
  const announcementText = ANNOUNCEMENTS[announcementIndex] ?? ANNOUNCEMENTS[0]
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => {
    const rotationInterval = setInterval(() => {
      setIsAnnouncementVisible(false)

      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current)
      }

      fadeTimeoutRef.current = setTimeout(() => {
        setAnnouncementIndex(
          (currentIndex) => (currentIndex + 1) % ANNOUNCEMENTS.length
        )
        requestAnimationFrame(() => {
          setIsAnnouncementVisible(true)
        })
      }, ANNOUNCEMENT_FADE_DURATION_MS)
    }, ANNOUNCEMENT_ROTATION_INTERVAL_MS)

    return () => {
      clearInterval(rotationInterval)

      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      {/* First under layout: dedicated fixed strip (not inside nav fixed wrapper). */}
      <div
        className="fixed top-0 inset-x-0 z-[60] isolate"
        style={{ height: ANNOUNCEMENT_BAR_HEIGHT_PX }}
      >
        <div className="border-y border-gold/30 bg-navy/95 text-cream shadow-sm shadow-stone-200/20 box-border flex h-full w-full items-center justify-center overflow-hidden px-3">
          <p
            aria-live="polite"
            title={announcementText}
            suppressHydrationWarning
            className={clx(
              "max-w-full text-[11px] small:text-xs font-medium tracking-wide text-center leading-tight transition-opacity duration-500",
              "line-clamp-2 break-words",
              isAnnouncementVisible ? "opacity-100" : "opacity-0"
            )}
          >
            {announcementText}
          </p>
        </div>
      </div>

      <div
        data-floating-nav-root
        className={clx("fixed inset-x-0 z-50", className)}
        style={{ top: ANNOUNCEMENT_BAR_HEIGHT_PX }}
      >
        <div className="max-w-[1440px] w-full mx-auto small:px-6 px-4">
          <div
            className={clx(
              "border border-stone-200 shadow-md shadow-stone-200/30",
              categoryRow ? "rounded-2xl" : "rounded-full"
            )}
          >
            <div
              className={clx(
                "relative z-[1] bg-cream/95 backdrop-blur-sm text-navy text-sm overflow-visible",
                "min-h-[40px] small:min-h-[44px]",
                "px-4 py-1.5",
                categoryRow ? "rounded-t-2xl border-b-0" : "rounded-full"
              )}
            >
              {children}
            </div>
            {categoryRow ? (
              <div className="overflow-visible rounded-b-2xl">{categoryRow}</div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Reserves room for the fixed announcement bar, nav pill, and category row. */}
      <div
        aria-hidden="true"
        className={clx(
          "min-h-[90px]",
          categoryRow ? "small:min-h-[128px]" : "small:min-h-[96px]"
        )}
      />
    </>
  )
}
